import { Injectable, inject, DOCUMENT } from '@angular/core';
import { Subject, filter, fromEvent, merge, tap } from 'rxjs';

import { FocusOutsideDirective } from '../../public/focus-outside.directive';

@Injectable({
  providedIn: 'root',
})
export class FocusOutsideService {
  private document = inject(DOCUMENT);

  private listeners = 0;
  private startX = 0;
  private startY = 0;

  private _focusChange$ = new Subject<EventTarget>();
  private focusChange$ = this._focusChange$.asObservable();

  private escPressed$ = fromEvent<KeyboardEvent>(
    this.document.body,
    'keydown',
    { capture: true },
  ).pipe(
    filter(({ key }) => ['Escape', 'Esc'].includes(key)),
    // Prevent propagation because we could be in dialog, which is listening for escape keydown (close)
    tap((e) => e.stopImmediatePropagation()),
  );

  private handleTouchStart = (event: TouchEvent) => {
    this.startX = event.changedTouches[0].clientX;
    this.startY = event.changedTouches[0].clientY;
  };

  private handleTouchEnd = (event: TouchEvent) => {
    const { clientX, clientY } = event.changedTouches[0];
    const distX = Math.abs(this.startX - clientX);
    const distY = Math.abs(this.startY - clientY);
    if (!distX && !distY && event.target) {
      this._focusChange$.next(event.target);
    }
  };

  private focusChange = (event: Event) => {
    if (event.target) {
      this._focusChange$.next(event.target);
    }
  };

  public monitor(dir: FocusOutsideDirective) {
    if (!this.listeners) {
      this.addListeners();
    }
    this.listeners++;
    return merge(
      this.focusChange$.pipe(
        filter((target) => this.isTargetOutside(dir, target)),
      ),
      this.escPressed$,
    );
  }

  public unmonitor() {
    this.listeners--;
    if (!this.listeners) {
      this.removeListeners();
    }
  }

  private isTargetOutside(dir: FocusOutsideDirective, target: EventTarget) {
    return (
      !(target instanceof HTMLElement) ||
      (target !== dir?.anchor &&
        !dir?.anchor?.contains(target) &&
        target !== dir.container &&
        !dir?.container?.contains(target))
    );
  }

  private addListeners() {
    window.addEventListener('touchstart', this.handleTouchStart, true);
    window.addEventListener('touchend', this.handleTouchEnd, true);
    window.addEventListener('click', this.focusChange, true);
    window.addEventListener('keydown', this.focusChange, true);
    window.addEventListener('focus', this.focusChange, true);
    window.addEventListener('blur', this.focusChange, true);
  }

  private removeListeners() {
    window.removeEventListener('touchstart', this.handleTouchStart, true);
    window.removeEventListener('touchend', this.handleTouchEnd, true);
    window.removeEventListener('click', this.focusChange, true);
    window.removeEventListener('keydown', this.focusChange, true);
    window.removeEventListener('focus', this.focusChange, true);
    window.removeEventListener('blur', this.focusChange, true);
  }
}
